import React, { useState, useCallback, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, IconButton, Zoom } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom ref={ref} {...props} />;
});

export default function ImagePreviewModal(props) {
    const { open, handleClose, imageSrc, title = 'Image Preview' } = props;
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const dialogContentRef = useRef(null);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(prevZoom => {
            const newZoom = Math.min(Math.max(prevZoom + delta, 0.5), 4);
            // Reset position if zooming back to 1 or below
            if (newZoom <= 1) {
                setPosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    }, []);

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = useCallback((e) => {
        if (isDragging && zoom > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart, zoom]);

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Reset position when zoom changes to 1 or below
    useEffect(() => {
        if (zoom <= 1) {
            setPosition({ x: 0, y: 0 });
        }
    }, [zoom]);

    // Prevent page scroll/zoom when modal is open
    useEffect(() => {
        const preventPageZoom = (e) => {
            if (open && e.ctrlKey) {
                e.preventDefault();
            }
        };

        const preventWheelOnModal = (e) => {
            if (open && dialogContentRef.current && dialogContentRef.current.contains(e.target)) {
                e.preventDefault();
            }
        };

        if (open) {
            document.addEventListener('wheel', preventWheelOnModal, { passive: false });
            document.addEventListener('keydown', preventPageZoom, { passive: false });
        }

        return () => {
            document.removeEventListener('wheel', preventWheelOnModal);
            document.removeEventListener('keydown', preventPageZoom);
        };
    }, [open]);

    const handleZoomIn = () => {
        setZoom(prevZoom => Math.min(prevZoom + 0.2, 4));
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoom - 0.2, 0.5);
        if (newZoom <= 1) {
            setPosition({ x: 0, y: 0 });
        }
        setZoom(newZoom);
    };

    const handleDialogClose = () => {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setIsDragging(false);
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            TransitionComponent={Transition}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    boxShadow: 'none',
                    width: '80vw',
                    height: '80vh',
                    maxWidth: '80vw',
                    maxHeight: '80vh',
                    margin: 'auto',
                }
            }}
            aria-labelledby="image-preview-dialog"
        >
            {/* Close button - top right with red background */}
            <IconButton
                onClick={handleDialogClose}
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    color: 'white',
                    backgroundColor: '#d32f2f',
                    '&:hover': {
                        backgroundColor: '#b71c1c',
                    },
                }}
                title="Close"
            >
                <CloseIcon />
            </IconButton>

            {/* Zoom controls - right side, vertically centered */}
            <Box
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <IconButton
                    onClick={handleZoomIn}
                    sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        },
                    }}
                    title="Zoom In"
                >
                    <AddIcon />
                </IconButton>
                <IconButton
                    onClick={handleZoomOut}
                    sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        },
                    }}
                    title="Zoom Out"
                >
                    <RemoveIcon />
                </IconButton>
            </Box>
            <DialogContent
                ref={dialogContentRef}
                sx={{
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    height: '100%',
                    width: '100%',
                }}
                onWheel={handleWheel}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                        userSelect: 'none',
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <img
                        src={imageSrc}
                        alt={title}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            pointerEvents: 'none',
                        }}
                        draggable={false}
                    />
                </Box>
            </DialogContent>
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'white',
                    fontSize: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    padding: '4px 12px',
                    borderRadius: '4px',
                }}
            >
                {Math.round(zoom * 100)}% - Scroll to zoom{zoom > 1 ? ', drag to pan' : ''}
            </Box>
        </Dialog>
    );
}
